import { ENTRY } from "./fields"
import { gql } from "@apollo/client"

export const _FORCE_UPDATE = gql`
  query _ForceUpdate {
    forceUpdate @client
  }
`

export const TEST_VALUE = gql`
  query GetTestValue {
    testValue @client {
      a
      b
    }
  }
`
export const CURRENT_ROUTE = gql`
  query IsCurrentRoute {
    currentRoute @client
  }
`

export const SIDEBAR_OPEN = gql`
  query IsSidebarOpen {
    sidebarOpen @client
  }
`
export const BEACON_OPEN = gql`
  query IsBeaconOpen {
    beaconOpen @client
  }
`
export const APPLICATION_ERROR = gql`
  query applicationError {
    error @client {
      title # individually set title for modal
      code # error code ie, auth/invalid-email
      message # message displayed to user
      internal # non-user friendly internal message
      field # related field in case of form
      ui # ui display target (toast | modal | alert)
      confirmBtn # Option confirm btn on bottom, set text of button
    }
  }
`

export const DOES_NOT_EXIST = gql`
  query DoesNotExist {
    doesNotExist @client
  }
`

export const USER_PLAN = gql`
  query IsUserPlan {
    userPlan @client
  }
`

export const SELECTED_UPGRADE = gql`
  query SelectedUpgradePlan {
    selectedUpgradePlan @client
  }
`

export const USER_GUIDES = gql`
  {
    userGuides @client
  }
`

/**
 * Query a review parent entry by it's year and/or month.  For monthly
 * reviews, provide both a month and year.  For yearly reviews, provide only
 * a year variable.
 */
export const REVIEW_PARENT_BY_DATE = gql`
  query GetReviewParentEntryByDate($year:String!, $month:String) {
    entry:reviewParentEntryByDate(year: $year, month:$month) @client {
      ${ENTRY}
    }
  }
`

export const MONTHLY_REVIEW = gql`
  query GetMonthlyReviewData {
    monthlyReview @client
  }
`

export const MONTHLY_REVIEW_NORMALIZED = gql`
  query GetMonthlyReviewNormalizedData {
    monthlyReview @client {
      id @client
      draft @client
      date @client
      month @client
      year @client
      index @client
      version @client
      highlightsNote @client
      lowlightsNotes @client {
        note @client
        question @client
        questionResponse @client
        entryKey @client
        updatedAt @client
      }
      rankings @client {
        mind @client {
          rank @client
          note @client
        }
        body @client {
          rank @client
          note @client
        }
        soul @client {
          rank @client
          note @client
        }
        work @client {
          rank @client
          note @client
        }
        play @client {
          rank @client
          note @client
        }
        love @client {
          rank @client
          note @client
        }
      }
      summarize @client
      forward @client
      saved @client
      entries @client {
        ${ENTRY}
      }
    }
  }
`

export const YEARLY_REVIEW = gql`
  query GetYearlyReviewData {
    yearlyReview @client
  }
`

export const YEARLY_REVIEW_NORMALIZED = gql`
  query GetYearlyReviewData {
    yearlyReview @client {
      id
      draft
      date
      year
      version
      lowlightsNotes {
        note
        question
        questionResponse
        entryKey
        updatedAt
      }
      rankings {
        mind {
          rank
          note
        }
        body {
          rank
          note
        }
        soul {
          rank
          note
        }
        work {
          rank
          note
        }
        play {
          rank
          note
        }
        love {
          rank
          note
        }
      }
      favorites {
        question
        subtext
        response
        type
      }
      highlightsQuestions {
        question
        subtext
        response
        type
      }
      lowlightsQuestions {
        question
        subtext
        response
        type
      }
      categoryQuestions {
        mind {
          question
          subtext
          response
          type
        }
        body {
          question
          subtext
          response
          type
        }
        soul {
          question
          subtext
          response
          type
        }
        work {
          question
          subtext
          response
          type
        }
        play {
          question
          subtext
          response
          type
        }
        love {
          question
          subtext
          response
          type
        }
      }
      revisionQuestions {
        question
        subtext
        response
        type
        fieldOnly
      }
      summaryQuestions {
        question
        subtext
        response
        type
      }
    }
  }
`

export const CURRENT_ENTRY = gql`
  query GetCurrentEntryData {
    currentEntry @client {
      ${ENTRY}
    }
  }
`

export const _GET_OPT_ENTRIES = gql`
  query _GetOptimisticUserEntries {
    optUserEntries @client
  }
`

export const _GET_OPT_ENTRIES_BY_ID = gql`
  query GetUserEntriesById($key: ID!) {
    entry: userEntriesById(key: $key) @client {
      key
      date
      type
      text
      image
      thumbnailImage
      pinned
      platform
      month
      year
      review
      tags
      reflection
      __typename
    }
  }
`

export const GET_CACHED_ENTRIES = gql`
  query GetCachedUserEntries {
    userEntries @client {
      ${ENTRY}
    }
  }
`

export const GET_ENTRIES = gql`
  query GetUserEntries {
    userEntries {
      ${ENTRY}
    }
  }
`

export const GET_ENTRIES_BY_STATUS = gql`
  query GetEntriesByStatus($premium: String, $free: String, $mobile: String) {
    totalEntries

    premium: entriesByUserCondition(conditions: $premium) {
      entries {
        key
        platform
      }
      total
    }

    free: entriesByUserCondition(conditions: $free) {
      entries {
        key
        platform
      }
      total
    }

    mobile: entriesByUserCondition(conditions: $mobile) {
      total
    }
  }
`

export const GET_ENTRIES_STATUS_VARS = {
  premium: '[[["premiumStatus", "==", true]],[]]',
  free: '[[["premiumStatus", "==", false]],[["platform", "==", "web"]]]',
  mobile: '[[],[["platform", "==", "mobile"]]]',
}

export const GET_QUOTES = gql`
  query GetAppQuotes {
    appQuotes {
      key
      text
      author
    }
  }
`

export const GET_MONTH_ENTRIES = gql`
  query GetUserEntriesByMonth($month: String, $year: String) {
    userEntriesByMonth(month: $month, year: $year) {
      ${ENTRY}
    }
  }
`

export const CHECK_PREMIUM = gql`
  query CheckPremiumUser {
    isPremiumUser {
      isPremium
      userPlan
    }
  }
`

/**
 * Runs right after successful authentication
 * checks if Firestore user is set up and if
 * not creates a new user document in Firestore.
 */
export const CREATE_USER = gql`
  query CreateUserCheck($source: String, $tp: String) {
    createUser(source: $source, tp: $tp) {
      success
      isNewUser
      error {
        code
        message
        internal
        ui
      }
    }
  }
`

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    currentUser {
      uid
      idToken
      update
      upvotyToken
      secureAuthToken
      settings
      premiumPlan
      premiumStatus
    }
  }
`

export const USER_INFORMATION = gql`
  query GetUserInformation {
    userInformation {
      uid
      email
      idToken
      update
      upvotyToken
      secureAuthToken
      settings
      premiumPlan
      premiumStatus
      premiumSubscriptionId
      premiumPurchaseSource
    }
  }
`

export const USER_DATA_STATE = gql`
  query GetUserDataState {
    userData @client {
      premiumPlan
      premiumStatus
      premiumSubscriptionId
      premiumPurchaseSource
    }
  }
`

export const GET_ENTRIES_BY = gql`
  query GetUserEntriesByCondition(
    $orderBy: String
    $key: String
    $type: String
    $month: String
    $year: String
    $pinned: String
  ) {
    userEntriesBy(
      condition: {
        orderBy: $orderBy
        key: $key
        type: $type
        month: $month
        year: $year
        pinned: $pinned
      }
    ) {
      ${ENTRY}
    }
  }
`
export const GET_EVENTS_BY = gql`
  query GetUserEventsBy(
    $user: String
    $eventName: String
    $month: String
    $year: String
  ) {
    userEventsBy(
      condition: {
        user: $user
        eventName: $eventName
        month: $month
        year: $year
      }
    ) {
      user
      eventName
      month
      year
    }
  }
`

export const TRACK_EVENT = gql`
  query TrackUserEvent($email: String, $eventName: String, $eventData: String) {
    trackUser(email: $email, eventName: $eventName, eventData: $eventData) {
      email
      success
      errors
    }
  }
`

export const IDENTIFY_USER = gql`
  query IdentifyUser($customProperties: String) {
    identifyUser(customProperties: $customProperties) {
      email
      success
    }
  }
`
export const EXPORT_DATA = gql`
  query ExportData {
    exportData {
      success
      json
      csv
      images
    }
  }
`

export const STRIPE_SESSION = gql`
  query($planId: String, $redirectURI: String) {
    sessionId: stripeCreateSession(planId: $planId, redirectURI: $redirectURI)
  }
`
export const STRIPE_BP_SESSION = gql`
  query($redirectURI: String) {
    stripeCreateBillingPortalSession(redirectURI: $redirectURI) {
      success
      url
    }
  }
`
